import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  InputAdornment,
  Tooltip,
  Chip,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EditIcon from '@mui/icons-material/Edit';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PersonIcon from '@mui/icons-material/Person';
import { usersApi, groupsApi } from '../services/api';

const debounce = (func, timeout = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
};

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [openNotifyDialog, setOpenNotifyDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [notification, setNotification] = useState({ title: '', body: '' });
  const [newTag, setNewTag] = useState('');
  const [groups, setGroups] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchUsers();
    fetchGroups();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await usersApi.getAllUsers();
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await groupsApi.getGroups();
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      showSnackbar('Failed to fetch groups', 'error');
    }
  };

  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (!query.trim()) {
        fetchUsers();
        return;
      }

      setSearchLoading(true);
      try {
        const response = await usersApi.searchUsers(query);
        setUsers(response.data.users);
      } catch (error) {
        console.error('Error searching users:', error);
        showSnackbar('Failed to search users', 'error');
      } finally {
        setSearchLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSearch(searchQuery);
  }, [searchQuery, debouncedSearch]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenNotifyDialog = (user) => {
    setSelectedUser(user);
    setOpenNotifyDialog(true);
  };

  const handleCloseNotifyDialog = () => {
    setOpenNotifyDialog(false);
    setSelectedUser(null);
    setNotification({ title: '', body: '' });
  };

  const handleOpenEditDialog = (user) => {
    setSelectedUser(user);
    setNewTag(user.groupTag || '');
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedUser(null);
    setNewTag('');
  };

  const handleSendNotification = async () => {
    try {
      await usersApi.sendNotification(selectedUser.id, notification.title, notification.body);
      handleCloseNotifyDialog();
      showSnackbar('Notification sent successfully');
    } catch (error) {
      console.error('Error sending notification:', error);
      showSnackbar('Failed to send notification', 'error');
    }
  };

  const handleUpdateTag = async () => {
    try {
      if (!newTag) {
        showSnackbar('Please select a group tag', 'error');
        return;
      }
      
      await usersApi.updateUserTag(selectedUser.id, newTag);
      await fetchUsers();
      handleCloseEditDialog();
      showSnackbar('User tag updated successfully');
    } catch (error) {
      console.error('Error updating user tag:', error);
      showSnackbar('Failed to update user tag', 'error');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: (theme) =>
            `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
          borderRadius: 3,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <PersonIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Users Management
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage user accounts, send notifications, and update user tags
              </Typography>
            </Box>
          </Box>
          <Box>
            <TextField
              placeholder="Search users..."
              size="small"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 300,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: (theme) => theme.palette.background.paper,
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Users Table */}
      {loading || searchLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : users.length === 0 ? (
        <Card
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.6)
                : alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(8px)',
            borderRadius: 2,
          }}
        >
          <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Users Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchQuery ? 'Try adjusting your search query' : 'No users have been added yet'}
          </Typography>
        </Card>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.6)
                : alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(8px)',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>User ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Android ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Group Tag</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow
                  key={user.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? alpha(theme.palette.primary.main, 0.1)
                          : alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <TableCell>{user.id}</TableCell>
                  <TableCell>{user.serial_android_id}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.groupTag || 'No Tag'}
                      size="small"
                      sx={{
                        backgroundColor: (theme) =>
                          user.groupTag
                            ? alpha(theme.palette.primary.main, 0.1)
                            : alpha(theme.palette.grey[500], 0.1),
                        color: (theme) =>
                          user.groupTag ? theme.palette.primary.main : theme.palette.grey[500],
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Send Notification">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenNotifyDialog(user)}
                          sx={{
                            color: 'primary.main',
                            '&:hover': {
                              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                            },
                          }}
                        >
                          <NotificationsIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit User Tag">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditDialog(user)}
                          sx={{
                            color: 'primary.main',
                            '&:hover': {
                              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Notification Dialog */}
      <Dialog
        open={openNotifyDialog}
        onClose={handleCloseNotifyDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.8)
                : alpha(theme.palette.background.paper, 0.9),
            backdropFilter: 'blur(8px)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Send Notification
          </Typography>
          <Typography variant="body2" color="text.secondary">
            to {selectedUser?.serial_android_id}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            value={notification.title}
            onChange={(e) => setNotification({ ...notification, title: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Message"
            fullWidth
            multiline
            rows={4}
            value={notification.body}
            onChange={(e) => setNotification({ ...notification, body: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={handleCloseNotifyDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSendNotification}
            variant="contained"
            disabled={!notification.title || !notification.body}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Tag Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.8)
                : alpha(theme.palette.background.paper, 0.9),
            backdropFilter: 'blur(8px)',
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <EditIcon sx={{ fontSize: 28, color: 'primary.main' }} />
            <Box>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                Edit User Tag
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedUser?.serial_android_id}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel sx={{ backgroundColor: 'transparent' }}>Group Tag</InputLabel>
            <Select
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              label="Group Tag"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              }}
            >
              {groups.map((group) => (
                <MenuItem 
                  key={group.groupTag} 
                  value={group.groupTag}
                  sx={{
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    },
                    '&.Mui-selected': {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.15),
                      '&:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                      },
                    },
                  }}
                >
                  <Box>
                    <Typography variant="body1">
                      {group.groupName || group.groupTag}
                    </Typography>
                    {group.groupName && (
                      <Typography variant="caption" color="text.secondary">
                        Tag: {group.groupTag}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
          <Button 
            onClick={handleCloseEditDialog} 
            color="inherit"
            sx={{
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.05),
              },
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateTag} 
            variant="contained"
            disabled={!newTag}
            sx={{
              px: 3,
              '&:not(:disabled)': {
                background: (theme) => 
                  `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersManagement;
